import { ChevronDown } from "lucide-react";
import { useId, useState } from "react";
import { matchPath, useLocation } from "react-router-dom";

import {
  SurveyDetailNavigationLink,
  surveyDetailNavigationItemClassName,
} from "./survey-detail-navigation-link";
import {
  getSurveyDetailPath,
  surveyDetailNavigation,
} from "~/features/surveys/lib/survey-detail-routes";
import { useTranslation } from "~/i18n";
import { cn } from "~/utils";

interface SurveyDetailNavigationProps {
  surveyUuid: string;
  onNavigate?: () => void;
}

export function SurveyDetailNavigation({
  surveyUuid,
  onNavigate,
}: SurveyDetailNavigationProps) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const mapsNavigationId = useId();
  const mapsActive = surveyDetailNavigation.mapsPhotos.routes.some((route) =>
    matchPath(
      { path: getSurveyDetailPath(surveyUuid, route), end: true },
      pathname,
    ),
  );
  const [mapsExpanded, setMapsExpanded] = useState(true);

  const toggleMapsNavigation = () => {
    setMapsExpanded((expanded) => !expanded);
  };

  return (
    <ul className="flex flex-col gap-[3px] lg:-mx-6">
      <li>
        <button
          type="button"
          aria-controls={mapsNavigationId}
          aria-expanded={mapsExpanded}
          onClick={toggleMapsNavigation}
          className={cn(
            surveyDetailNavigationItemClassName,
            "cursor-pointer justify-between",
            mapsActive ? "bg-white/30 text-foreground" : "text-white",
          )}
        >
          <span className="lg:ml-6">
            {t(surveyDetailNavigation.mapsPhotos.labelKey)}
          </span>
          <ChevronDown
            aria-hidden="true"
            className={cn(
              "mr-1 size-5 stroke-[3] transition-transform motion-reduce:transition-none lg:mr-[18px]",
              !mapsExpanded && "-rotate-90",
            )}
          />
        </button>

        <ul
          id={mapsNavigationId}
          hidden={!mapsExpanded}
          className={cn(
            "mt-[3px] flex flex-col gap-[3px]",
            !mapsExpanded && "hidden",
          )}
        >
          {surveyDetailNavigation.mapsPhotos.routes.map((route) => (
            <li key={route.path}>
              <SurveyDetailNavigationLink
                indented
                route={route}
                surveyUuid={surveyUuid}
                onNavigate={onNavigate}
              />
            </li>
          ))}
        </ul>
      </li>

      <li>
        <SurveyDetailNavigationLink
          route={surveyDetailNavigation.report}
          surveyUuid={surveyUuid}
          onNavigate={onNavigate}
        />
      </li>
    </ul>
  );
}
